/**
 * SectionCard — Container léger pour une section de page (config, stats, table…).
 *
 * Vague 3 lot 2 (2026-09-01) : remplace les innombrables
 * `<div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">`
 * dispersés dans les pages Notes & Saisie. Titre optionnel, icône optionnelle,
 * actions à droite du header, padding personnalisable.
 *
 * Différence avec `Card` (existant) : plus léger visuellement (border simple au
 * lieu d'une ombre marquée), header intégré, taille par défaut compacte.
 * Card reste utilisée là où l'ombre marquée fait sens (dashboards).
 */
import React from 'react';

export interface SectionCardProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Padding autour du contenu — défaut 'p-4', ou 'p-0' pour tables full-width */
  padding?: 'p-0' | 'p-3' | 'p-4' | 'p-6';
}

export const SectionCard: React.FC<SectionCardProps> = ({
  title, subtitle, icon, actions, children,
  className = '', bodyClassName = '', padding = 'p-4',
}) => {
  const hasHeader = !!(title || subtitle || actions);
  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm ${className}`}
    >
      {hasHeader && (
        <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          {icon && <div className="text-gray-400 dark:text-gray-500 flex-shrink-0">{icon}</div>}
          <div className="min-w-0 flex-1">
            {title && (
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                {title}
              </h3>
            )}
            {subtitle && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                {subtitle}
              </div>
            )}
          </div>
          {actions && <div className="flex items-center gap-1.5 flex-shrink-0">{actions}</div>}
        </div>
      )}
      <div className={`${padding} ${bodyClassName}`}>{children}</div>
    </div>
  );
};

export default SectionCard;
